import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import FilteredList from "./filteredList";
import * as F from "./filter.styles";
import SizeFilter from "../../components/ecommerce/Filter/SizeFilter";
import { useRouter } from "next/router";
import { getCookie } from "../auth";

// 라우팅 없는 필터 생성
const Filter = forwardRef((props, ref) => {
  const {
    dummyList,
    productList,
    fixedViewType,
    purchaseDirect,
    handleCart,
    basketsItems,
    cartItems,
    product,
  } = props;
  const router = useRouter();
  const [filteredList, setFilteredList] = useState(null);
  const [filters, setFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState({});
  const [viewType, setViewType] = useState("");

  // 카테고리별 세부 카테고리를 저장할 상태 추가
  const [categoryTagMap, setCategoryTagMap] = useState({});

  const handleListViewType = (type) => {
    if (viewType === type) {
      return;
    }
    localStorage.setItem("P_viewType", type);
    setViewType(type);
  };

  // 필터
  const handleFilter = (type, value) => {
    if (type === "sizes") {
      const { sizes, ...rest } = activeFilter;
      setActiveFilter({ ...rest });
      return;
    }

    // reset 처리 추가
    if (type === "reset") {
      setActiveFilter({});
      return;
    }

    const prevQuery = activeFilter[type];
    let query = "";

    if (prevQuery && prevQuery.split(" ").includes(value)) {
      const tempArr = prevQuery.split(" ");
      const idx = prevQuery.split(" ").findIndex((el) => el === value);

      tempArr.splice(idx, 1);
      query = `${tempArr.join(" ")}`;
    } else {
      if (activeFilter[type]) {
        query = `${prevQuery} ${value}`;
      } else {
        query = value;
      }
    }

    // 카테고리 변경 시 세부 카테고리 리셋
    if (type === "category_code") {
      // 카테고리가 완전히 비워졌는지 체크
      if (query === "") {
        setActiveFilter({
          ...activeFilter,
          [type]: query,
          category_tag: "",
        });
      } else {
        setActiveFilter({
          ...activeFilter,
          [type]: query,
          category_tag: "",
        });
      }
    } else {
      if (!activeFilter[type]) {
        setActiveFilter({
          ...activeFilter,
          [type]: value,
        });
      } else {
        setActiveFilter({
          ...activeFilter,
          [type]: query,
        });
      }
    }
  };

  // 필터 만들기
  const getFilters = () => {
    const productsCate = [];
    let categoryFilter = {
      type: "category_code",
      name: "카테고리",
      values: [],
    };

    // 카테고리별 세부 카테고리 매핑 생성
    const categoryTagMapping = {};

    productList.forEach((el) => {
      if (!productsCate.includes(el.category_code)) {
        productsCate.push(el.category_code);
        categoryTagMapping[el.category_code] = [];
      }

      // 해당 카테고리의 세부 카테고리 태그들 수집
      if (el.category_tag) {
        const tags = el.category_tag.split(",");
        tags.forEach((tag) => {
          if (!categoryTagMapping[el.category_code].includes(tag)) {
            categoryTagMapping[el.category_code].push(tag);
          }
        });
      }
    });

    // 카테고리별 세부 카테고리 저장
    setCategoryTagMap(categoryTagMapping);

    const categories = dummyList?.category?.filter((el) =>
      productsCate.includes(el.code)
    );

    if (categories) {
      categoryFilter = {
        type: "category_code",
        name: "카테고리",
        values: categories.map((el) => {
          return { name: el.name, value: el.code };
        }),
      };
    }

    // 세부 카테고리 필터 생성 (초기에는 모든 세부 카테고리 표시)
    const allCategoryTags = [];
    Object.values(categoryTagMapping).forEach((tags) => {
      tags.forEach((tag) => {
        if (!allCategoryTags.includes(tag)) {
          allCategoryTags.push(tag);
        }
      });
    });

    const categoryTagFilter = {
      type: "category_tag",
      name: "세부 카테고리",
      values: allCategoryTags.map((el) => {
        return { name: el, value: el };
      }),
    };

    setFilters({
      ...filters,
      category_code: categoryFilter,
      category_tag: categoryTagFilter,
    });
  };

  // 현재 선택된 카테고리에 해당하는 세부 카테고리 필터 가져오기
  const getFilteredCategoryTags = () => {
    const selectedCategory = activeFilter["category_code"];

    // 선택된 카테고리가 없으면 모든 세부 카테고리 반환
    if (!selectedCategory) {
      const allTags = [];
      Object.values(categoryTagMap).forEach((tags) => {
        tags.forEach((tag) => {
          if (!allTags.includes(tag)) {
            allTags.push(tag);
          }
        });
      });

      return {
        type: "category_tag",
        name: "세부 카테고리",
        values: allTags.map((tag) => ({ name: tag, value: tag })),
      };
    }

    // 선택된 카테고리의 세부 카테고리만 필터링
    const selectedCategories = selectedCategory.split(" ");
    const filteredTags = [];

    selectedCategories.forEach((category) => {
      if (categoryTagMap[category]) {
        categoryTagMap[category].forEach((tag) => {
          if (!filteredTags.includes(tag)) {
            filteredTags.push(tag);
          }
        });
      }
    });

    return {
      type: "category_tag",
      name: "세부 카테고리",
      values: filteredTags.map((tag) => ({ name: tag, value: tag })),
    };
  };

  // active 필터 적용
  const filtering = () => {
    if (
      Object.keys(activeFilter) &&
      Object.values(activeFilter).filter((el) => el != "").length > 0
    ) {
      const query = activeFilter;
      const filterKeys = Object.keys(activeFilter);
      let tempList = productList;
      filterKeys.map((key) => {
        if (!query[key]) return;
        tempList = tempList.filter((el) => {
          if (!query[key]) return;
          if (key === "sizes") {
            const { height, horizontal, range, vertical } = JSON.parse(
              query.sizes
            );
            if (
              (horizontal > 0 &&
                Number(horizontal) - Number(range) <= Number(el.horizontal) &&
                Number(el.horizontal) <= Number(horizontal) + Number(range)) ||
              (vertical > 0 &&
                Number(vertical) - Number(range) <= Number(el.vertical) &&
                Number(el.vertical) <= Number(vertical) + Number(range)) ||
              (height > 0 &&
                Number(height) - Number(range) <= Number(el.height) &&
                Number(el.height) <= Number(height) + Number(range))
            ) {
              return el;
            }
          } else if (
            key === "category_tag" &&
            el[key].split(",").includes(query[key])
          ) {
            return el;
          } else if (query[key].split(" ").includes(el[key])) {
            return el;
          } else {
            return;
          }
        });
      });
      setFilteredList(tempList);
    } else {
      setFilteredList(productList);
    }
  };

  const handleSizeFilter = (sizes) => {
    const { height, horizontal, range, vertical } = sizes;
    if (sizes) {
      setActiveFilter({
        ...activeFilter,
        sizes: JSON.stringify(sizes),
      });
    }
  };

  // 카트 담긴 상태
  const inCart = (product) => {
    if (!getCookie("token")) {
      return cartItems?.find((cartItem) => cartItem.pcode === product.pcode);
    } else {
      return basketsItems?.find(
        (basketsItem) => basketsItem.pcode === product.pcode
      );
    }
  };

  useEffect(() => {
    filtering();
  }, [activeFilter, productList]);

  useEffect(() => {
    if (!productList || !dummyList) return;
    getFilters();
    if (fixedViewType) {
      setViewType(fixedViewType);
    }
  }, [productList, dummyList, fixedViewType]);

  // 외부에서 접근 가능한 함수 정의
  useImperativeHandle(ref, () => ({
    handleFilterClick: (type, value) => {
      handleFilter(type, value);
    },
  }));

  // 초기 필터 자동 세팅을 위한 함수
  const setInitialFilters = () => {
    if (!product || !filters) return;

    const initialFilter = {};

    // category_code 자동 세팅
    if (product.category_code && filters.category_code) {
      // category_code가 문자열이므로 직접 비교
      const matchingCategory = filters.category_code.values.find(
        (val) => val.name === product.category_code
      );

      if (matchingCategory) {
        // value 값으로 필터 세팅
        initialFilter.category_code = matchingCategory.value;
      }
    }

    // category_tag 자동 세팅
    if (product.category_tag && filters.category_tag) {
      // 첫 번째 태그를 기본값으로 사용
      const firstTag = Array.isArray(product.category_tag)
        ? product.category_tag[0]
        : product.category_tag.split(",")[0];

      const matchingTag = filters.category_tag.values.find(
        (val) => val.value === firstTag
      );
      if (matchingTag) {
        initialFilter.category_tag = firstTag;
      }
    }

    // 초기 필터 값 설정
    if (Object.keys(initialFilter).length > 0) {
      setActiveFilter(initialFilter);
    }
  };

  // 필터 생성 후 초기 필터 세팅
  useEffect(() => {
    if (Object.keys(filters).length > 0 && product) {
      setInitialFilters();
    }
  }, [filters, product]);

  // 현재 선택된 카테고리에 따라 필터링된 세부 카테고리 가져오기
  const filteredCategoryTags = getFilteredCategoryTags();

  return (
    <>
      {Object.keys(filters)?.length > 0 && (
        <F.FilterWrapper>
          {/* 카테고리 필터 */}
          {filters.category_code &&
            filters.category_code.values?.length > 0 && (
              <F.FilterRow>
                <F.FilterRowTitle>
                  {filters.category_code.name}
                </F.FilterRowTitle>
                <F.FilterRowList>
                  {filters.category_code.values.map((row, rowIdx) => (
                    <li key={rowIdx}>
                      <a
                        onClick={() =>
                          handleFilter(filters.category_code.type, row.value)
                        }
                        className={`${
                          activeFilter[filters.category_code.type] &&
                          activeFilter[filters.category_code.type].includes(
                            row.value
                          ) &&
                          "active"
                        }`}
                      >
                        {row.name}
                        {activeFilter[filters.category_code.type] &&
                          activeFilter[filters.category_code.type].includes(
                            row.value
                          ) && (
                            <span>
                              <i className="fi-rs-cross" />
                            </span>
                          )}
                      </a>
                    </li>
                  ))}
                </F.FilterRowList>
              </F.FilterRow>
            )}

          {/* 세부 카테고리 필터 - 필터링된 세부 카테고리 사용 */}
          {filteredCategoryTags.values?.length > 0 && (
            <F.FilterRow>
              <F.FilterRowTitle>{filteredCategoryTags.name}</F.FilterRowTitle>
              <F.FilterRowList>
                {filteredCategoryTags.values.map((row, rowIdx) => (
                  <li key={rowIdx}>
                    <a
                      onClick={() =>
                        handleFilter(filteredCategoryTags.type, row.value)
                      }
                      className={`${
                        activeFilter[filteredCategoryTags.type] &&
                        activeFilter[filteredCategoryTags.type].includes(
                          row.value
                        ) &&
                        "active"
                      }`}
                    >
                      {row.name}
                      {activeFilter[filteredCategoryTags.type] &&
                        activeFilter[filteredCategoryTags.type].includes(
                          row.value
                        ) && (
                          <span>
                            <i className="fi-rs-cross" />
                          </span>
                        )}
                    </a>
                  </li>
                ))}
              </F.FilterRowList>
            </F.FilterRow>
          )}

          <F.FilterRow>
            <F.FilterRowTitle>사이즈</F.FilterRowTitle>
            <F.FilterRowList>
              <div className="d-flex p-10">
                <SizeFilter
                  handleSizeFilter={handleSizeFilter}
                  viewHeight={true}
                  selectedCategory={activeFilter.category_code}
                />
              </div>
            </F.FilterRowList>
          </F.FilterRow>
          {activeFilter &&
            Object.values(activeFilter).filter((el) => el != "").length > 0 && (
              <F.FilteredListWrapper>
                <div className="all">
                  <a
                    onClick={() => {
                      setActiveFilter({});
                    }}
                  >
                    전체 해제 <i className="fi-rs-rotate-left" />{" "}
                  </a>
                </div>
                <F.CheckedFilters>
                  {Object.keys(activeFilter)?.length > 0 &&
                    Object.keys(activeFilter).map((query) => {
                      if (query !== "sizes") {
                        if (query === "category_code") {
                          return filters[query]?.values.map((value, vIdx) => {
                            if (
                              activeFilter[query]
                                .split(" ")
                                .includes(value.value)
                            ) {
                              return (
                                <li
                                  key={vIdx}
                                  onClick={() =>
                                    handleFilter(query, value.value)
                                  }
                                >
                                  <a>{value.name}</a>
                                </li>
                              );
                            } else {
                              return "";
                            }
                          });
                        } else if (query === "category_tag") {
                          return filteredCategoryTags.values.map(
                            (value, vIdx) => {
                              if (
                                activeFilter[query]
                                  .split(" ")
                                  .includes(value.value)
                              ) {
                                return (
                                  <li
                                    key={vIdx}
                                    onClick={() =>
                                      handleFilter(query, value.value)
                                    }
                                  >
                                    <a>{value.name}</a>
                                  </li>
                                );
                              } else {
                                return "";
                              }
                            }
                          );
                        }
                      } else {
                        return (
                          <li onClick={() => handleFilter(query, "")}>
                            <a>사이즈 필터</a>
                          </li>
                        );
                      }
                    })}
                </F.CheckedFilters>
              </F.FilteredListWrapper>
            )}
        </F.FilterWrapper>
      )}
      <FilteredList
        viewType={viewType}
        handleListViewType={handleListViewType}
        dummyList={dummyList}
        filteredList={filteredList}
        purchaseDirect={purchaseDirect}
        handleCart={handleCart}
        inCart={inCart}
        fixedViewType={fixedViewType}
      />
    </>
  );
});

Filter.displayName = "Filter";

export default Filter;
